import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { processTransferTask } from "./transfer";

export interface TransferTask {
  id: string;
  type: "upload" | "download";
  status: "pending" | "in-progress" | "completed" | "failed";
  name: string;
  file?: File;
  remoteKey: string;
  loaded: number;
  total: number;
  error?: Error;
}

const TransferQueueContext = createContext<TransferTask[]>([]);
const SetTransferQueueContext = createContext<
  React.Dispatch<React.SetStateAction<TransferTask[]>>
>(() => {});

export function useTransferQueue() {
  return useContext(TransferQueueContext);
}

export function useUploadEnqueue() {
  const setTransferTasks = useContext(SetTransferQueueContext);

  return (...requests: { basedir: string; file: File }[]) => {
    const newTasks: TransferTask[] = requests.map(({ basedir, file }) => ({
      id: `${Date.now()}-${Math.random().toString(36).substring(7)}`,
      type: "upload",
      status: "pending",
      name: file.name,
      file,
      remoteKey: basedir + file.name,
      loaded: 0,
      total: file.size,
    }));

    setTransferTasks((tasks) => [...tasks, ...newTasks]);
  };
}

interface TransferQueueProviderProps {
  children: React.ReactNode;
}

export function TransferQueueProvider({
  children,
}: TransferQueueProviderProps) {
  const [transferTasks, setTransferTasks] = useState<TransferTask[]>([]);
  const taskProcessing = useRef<TransferTask | null>(null);

  function currentTaskUpdater(props: Partial<TransferTask>) {
    const currentTask = taskProcessing.current;
    return (tasks: TransferTask[]) => {
      const newTask = { ...currentTask!, ...props };
      const newTasks = tasks.map((t) =>
        t === taskProcessing.current ? newTask : t
      );
      if (currentTask === taskProcessing.current)
        taskProcessing.current = newTask;
      return newTasks;
    };
  }

  useEffect(() => {
    const taskToProcess = transferTasks.find(
      (task) => task.status === "pending"
    );

    if (!taskToProcess || taskProcessing.current) return;

    taskProcessing.current = taskToProcess;
    setTransferTasks(currentTaskUpdater({ status: "in-progress" }));

    processTransferTask({
      task: taskToProcess,
      onTaskProgress: ({ loaded, total }) => {
        setTransferTasks(currentTaskUpdater({ loaded, total }));
      },
    })
      .then(() => {
        setTransferTasks(currentTaskUpdater({ status: "completed", loaded: taskToProcess.total }));
        taskProcessing.current = null;
      })
      .catch((error) => {
        console.error("Transfer failed:", error);
        setTransferTasks(currentTaskUpdater({ status: "failed", error }));
        taskProcessing.current = null; // Fix: Reset processing ref on error
      });
  }, [transferTasks]);

  return (
    <TransferQueueContext.Provider value={transferTasks}>
      <SetTransferQueueContext.Provider value={setTransferTasks}>
        {children}
      </SetTransferQueueContext.Provider>
    </TransferQueueContext.Provider>
  );
}
